import { useState } from 'react';
import { Link, Navigate, useLocation, useParams } from 'react-router-dom';
import { Check, Sparkles } from 'lucide-react';

import { Button } from '@/components/ui/button';
import { CompTag } from '@/components/CompTag';
import { LoomliteMark } from '@/components/LoomliteMark';
import { useCompetition } from '@/hooks/useCompetitions';
import { formatDate } from '@/lib/utils';

interface ThanksState {
	entityType?: string;
	entityTitle?: string;
	entryId?: string | null;
	submittedAt?: string;
}

function BigCheck() {
	return (
		<div className="relative mx-auto h-[120px] w-[120px]">
			<div
				className="absolute -inset-6 rounded-full"
				style={{ background: 'radial-gradient(circle, rgba(5,206,120,0.25) 0%, transparent 65%)' }}
			/>
			<div
				className="absolute inset-0 flex items-center justify-center rounded-full text-loomlite-ink"
				style={{
					background: 'linear-gradient(135deg, var(--color-loomlite-green) 0%, #00a860 100%)',
					boxShadow: '0 12px 40px rgba(5,206,120,0.3)',
				}}
			>
				<Check className="h-14 w-14" strokeWidth={3} />
			</div>
			<svg
				className="absolute"
				style={{ top: -8, left: 20 }}
				width="14"
				height="14"
				viewBox="0 0 24 24"
				fill="var(--color-loomlite-gold)"
			>
				<path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
			</svg>
			<svg
				className="absolute"
				style={{ top: 18, right: -10 }}
				width="10"
				height="10"
				viewBox="0 0 24 24"
				fill="var(--color-loomlite-gold)"
			>
				<path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
			</svg>
			<svg
				className="absolute"
				style={{ bottom: -6, left: 0 }}
				width="8"
				height="8"
				viewBox="0 0 24 24"
				fill="var(--color-loomlite-gold)"
			>
				<path d="M12 2 L14 10 L22 12 L14 14 L12 22 L10 14 L2 12 L10 10 Z" />
			</svg>
		</div>
	);
}

function TimelineStep({
	label,
	when,
	state,
	isLast,
}: {
	label: string;
	when: string;
	state: 'done' | 'active' | 'pending';
	isLast?: boolean;
}) {
	const color =
		state === 'done'
			? 'var(--color-loomlite-green)'
			: state === 'active'
				? 'var(--color-loomlite-gold)'
				: 'var(--color-loomlite-dim)';
	return (
		<li className="flex items-start gap-3">
			<div className="relative flex-shrink-0">
				<div
					className="flex h-[22px] w-[22px] items-center justify-center rounded-full border-[1.5px]"
					style={{
						borderColor: color,
						background: state === 'done' ? color : 'transparent',
						color: 'var(--color-loomlite-ink)',
					}}
				>
					{state === 'done' && <Check className="h-2.5 w-2.5" strokeWidth={3.5} />}
					{state === 'active' && (
						<div className="h-2 w-2 rounded-full" style={{ background: color }} />
					)}
				</div>
				{!isLast && (
					<div
						className="absolute left-[10px] top-[22px] -bottom-3.5 w-[2px]"
						style={{ background: 'var(--color-loomlite-border)' }}
					/>
				)}
			</div>
			<div>
				<div
					className="text-sm font-medium"
					style={{
						color:
							state === 'pending' ? 'var(--color-loomlite-muted)' : 'var(--color-loomlite-text)',
					}}
				>
					{label}
				</div>
				<div className="mt-0.5 text-xs text-loomlite-dim">{when}</div>
			</div>
		</li>
	);
}

export function ThanksPage() {
	const { slug } = useParams();
	const location = useLocation();
	const state = (location.state ?? {}) as ThanksState;
	const { data: competition, isLoading } = useCompetition(slug);

	/*
	 * Read once at mount, and ABOVE the early returns below -- a hook after one is
	 * a conditional hook, which is how the first attempt at this traded a purity
	 * finding for a worse rules-of-hooks one.
	 *
	 * `Date.now()` during render is impure, and this only drives which step of the
	 * timeline reads as active. It does not need to tick, and nothing re-renders
	 * this page on a timer anyway.
	 */
	const [now] = useState(() => Date.now());

	if (isLoading) return <p className="p-8 text-loomlite-muted">Loading…</p>;
	if (!competition) return <Navigate to="/" replace />;

	const submittedAt = state.submittedAt ? new Date(state.submittedAt) : new Date();
	const entryShortId = state.entryId ? `#${state.entryId.slice(0, 8).toUpperCase()}` : null;

	const closeTs = new Date(competition.SubmissionCloseAt).getTime();
	const announceTs = competition.AnnouncementAt
		? new Date(competition.AnnouncementAt).getTime()
		: null;

	return (
		<div className="min-h-screen">
			<header className="border-b border-loomlite-border bg-loomlite-ink/50 backdrop-blur">
				<div className="mx-auto flex max-w-6xl items-center justify-between px-7 py-3.5">
					<LoomliteMark size={24} />
					<CompTag color="gold">{competition.Name}</CompTag>
				</div>
			</header>

			<main className="mx-auto grid max-w-6xl grid-cols-1 items-start gap-10 px-7 py-12 lg:grid-cols-2 lg:gap-12">
				<div>
					<BigCheck />
					<div className="mt-7">
						<CompTag color="green">
							Entry accepted{entryShortId ? ` · ${entryShortId}` : ''}
						</CompTag>
						<h1 className="m-0 mt-4 font-heading text-5xl font-bold leading-[1.05] tracking-tight">
							You're in the
							<br />
							<span className="ll-gradient-text">compendium.</span>
						</h1>
						<p className="m-0 mt-3.5 max-w-[460px] text-[15px] leading-relaxed text-loomlite-muted">
							<strong className="font-semibold text-loomlite-text">
								{state.entityTitle ?? 'Your entry'}
							</strong>{' '}
							has been accepted into the {competition.Name}. You can withdraw any time before{' '}
							{formatDate(competition.SubmissionCloseAt)}: the judges won't see entries until
							submissions close.
						</p>
					</div>

					<div className="mt-6 flex gap-2.5">
						<Button asChild>
							<Link to="/my-submissions">View my entries</Link>
						</Button>
						<Button variant="outline">
							<Sparkles className="h-4 w-4" /> Share on Discord
						</Button>
					</div>
				</div>

				<div className="flex flex-col gap-4">
					<div className="rounded-xl border border-loomlite-border bg-loomlite-card p-5">
						<div className="mb-3 text-[11px] uppercase tracking-[0.1em] text-loomlite-dim">
							Submission receipt
						</div>
						<div
							className="space-y-1.5 text-xs text-loomlite-muted"
							style={{ fontFamily: 'ui-monospace, "SF Mono", Menlo, monospace', lineHeight: 1.8 }}
						>
							{entryShortId && (
								<div className="flex justify-between">
									<span>ENTRY ID</span>
									<span className="text-loomlite-text">{entryShortId}</span>
								</div>
							)}
							{state.entityType && (
								<div className="flex justify-between">
									<span>TYPE</span>
									<span className="text-loomlite-text">{state.entityType}</span>
								</div>
							)}
							{state.entityTitle && (
								<div className="flex justify-between">
									<span>TITLE</span>
									<span className="ml-4 truncate text-loomlite-text">{state.entityTitle}</span>
								</div>
							)}
							<div className="flex justify-between">
								<span>SUBMITTED</span>
								<span className="text-loomlite-text">{formatDate(submittedAt.toISOString())}</span>
							</div>
							<div className="flex justify-between">
								<span>STATUS</span>
								<span className="text-loomlite-green">● Accepted</span>
							</div>
						</div>
					</div>

					<div className="rounded-xl border border-loomlite-border bg-loomlite-card p-5">
						<div className="mb-3.5 text-[11px] uppercase tracking-[0.1em] text-loomlite-dim">
							What happens next
						</div>
						<ol className="m-0 flex list-none flex-col gap-3.5 p-0">
							<TimelineStep label="Entry received" when="Now" state="done" />
							<TimelineStep
								label="Submissions close"
								when={formatDate(competition.SubmissionCloseAt)}
								state={now < closeTs ? 'active' : 'done'}
							/>
							<TimelineStep
								label="Judging window"
								when={`Opens ${formatDate(competition.SubmissionCloseAt).split(',')[0]}`}
								state={
									now < closeTs ? 'pending' : announceTs && now < announceTs ? 'active' : 'done'
								}
							/>
							{competition.AnnouncementAt && (
								<TimelineStep
									label="Winners announced"
									when={formatDate(competition.AnnouncementAt)}
									state={announceTs && now >= announceTs ? 'done' : 'pending'}
									isLast
								/>
							)}
						</ol>
					</div>

					<div className="rounded-[10px] border border-loomlite-purple/25 bg-loomlite-purple/[0.08] p-4 text-sm leading-relaxed text-loomlite-muted">
						<strong className="text-loomlite-text">One more thing.</strong> We'll email you a copy
						of this receipt and a reminder the day before submissions close.
					</div>
				</div>
			</main>
		</div>
	);
}
